import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, NgForm, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { UsersService } from '../_services/users.service';
import { User } from '../_types/user';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { LanguagesService } from '../../common/_services/languages.service';
import { RolesService } from '../../roles/_services/roles.service';
import { UserAuthenticationService } from '../_types/userAuthenticationService';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { Filter, FilterOperation, NotFilter } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { UserAuthenticationServicesService } from '../_services/userAuthenticationService.service';
import { DialogService } from 'primeng/dynamicdialog';
import { AuthenticationServiceComponent } from '../authentication-service/authentication-service.component';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { DDI } from '../_types/ddi';
import { DDIsService } from '../_services/ddis.service';
import { StringUtilsService } from 'src/app/core/services/string-utils.service';
import { UserType } from '../_types/user-type';
import { UserTypesService } from '../_services/users-types.service';
import { GroupUsersService } from '../../groups/_services/groups-users.service';
import { UserGroupComponent } from './user-group/user-group.component';
import { ExtensionsService } from '../../voip/_services/extensions.service';
import { LoginService } from '../../login/login.service';


@Component({
    selector: 'app-users-detail',
    templateUrl: './users-detail.component.html',
    styleUrls: ['./users-detail.component.scss'],
    standalone: false
})
export class UsersDetailComponent implements OnInit {

  private static readonly API_USER_TYPE_CODE = 'api';

  public userFormGroup!: RxFormGroup;

  public user: User = new User()
  public userTypes: UserType[] = [];
  public userTypeSelected: UserType = new UserType();

  public languages: any[] = [];
  public roles: any[] = [];
  public groups: any[] = [];
  public ddis: DDI[] = [];
  public userAuthenticationServices: UserAuthenticationService[] = [];
  public loading: boolean = false;
  public totalRecords: number = 0;
  public totalGroupsRecords: number = 0;
  public sipTransports: any[] = [
    { name: 'UDP 8086 (Por defecto)', id: 'transport-udp-8086' },
    { name: 'UDP 5060', id: 'transport-udp' },
    { name: 'TCP 5060', id: 'transport-tcp' },
    { name: 'TCP 8086', id: 'transport-tcp-8086' }
  ];

  public allowWrite: boolean = false;
  public allowDelete: boolean = false;
  public set_new_password:boolean = false;
  public visibleChangePass: boolean = false;
  public password: string = "";
  public changeUser?: User;
  public users: User[] = [];

  //contraseña de la extensión recién generada en edición. La API no devuelve la guardada, así que
  //solo puede mostrarse en el momento de generarla para configurar el teléfono/MicroSIP.
  public generatedSipExtensionPassword: string | null = null;
  public usersTechnicals: User[] = [];
  public menuItems: MenuItem[] = [];

  constructor(
    private route: ActivatedRoute,
    private usersService: UsersService,
    private router: Router,
    private messageService: MessageService,
    public topbarService: TopbarService,
    private confirmationService: ConfirmationService,
    private languagesService: LanguagesService,
    private userTypesService: UserTypesService,
    private rolesService: RolesService,
    private groupUsersService: GroupUsersService,
    private ddisService: DDIsService,
    private usersAuthenticationServicesService: UserAuthenticationServicesService,
    private dialogService: DialogService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private stringUtils: StringUtilsService,
    private extensionService: ExtensionsService,
    private loginService: LoginService
  ) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    this.userFormGroup = this.formBuilder.formGroup<User>(this.user) as RxFormGroup;
  }

  ngOnInit(): void {
    this.topbarService.setTitle("Detalles de usuario");
    this.topbarService.addBreadcrumb("Usuarios", "/users");
    this.route.paramMap.subscribe((params: ParamMap) => {
      let id: string | null = params.get('guid');
      if (id != null && id != '0') {
        this.loadUser(id);
      }else{
        //por defecto TCP
        this.user.sipTransport = 'transport-tcp';
        //alta: generamos la contraseña de la extensión. En edición NO, para no cambiarle la
        //contraseña del teléfono en cada guardado (la API ya no devuelve el campo). Se escribe
        //sobre el control del formulario porque save() envía userFormGroup.value, no this.user.
        this.userFormGroup.get('sipExtensionPassword')?.setValue(this.stringUtils.RandomString(8));
        this.loadUsers();
      }
      this.loadLanguages();
      this.loadRoles();
      this.loadUserTypes();
      this.loadDDIs();
    });
    this.allowWrite = this.loginService.hasPermission("USERS.WRITE");
    this.allowDelete = this.loginService.hasPermission("USERS.DELETE");

    //este array se construye antes de que termine loadUser, así que la entrada de uso de API se añade
    //después, cuando ya se conoce el tipo de usuario. La acción del topbar guarda esta misma referencia,
    //así que basta con añadirla a la lista para que el menú la muestre
    this.menuItems = [
      { label: 'Actividad de usuario', icon: 'pi pi-clipboard', command: () => this.userActivityReport()}
    ]
    this.topbarService.addAction("Acciones masivas", "pi pi-ellipsis-v",false, () => {
      
    }, this.menuItems);  
  }

  async loadUser(id: string) {
    this.user = await firstValueFrom(this.usersService.get(id, { include: ['userType', 'secondUser','workerUser'] }));
  

    //si no tiene trasnporte sip, le asignamos el por defecto
    if(this.user.sipTransport == null || this.user.sipTransport == ''){
      this.user.sipTransport = 'transport-tcp';
    }

    //desactivamos validacion del password
    this.userFormGroup.get('password')?.disable();

    //en edición NO se genera contraseña de extensión (eso solo se hace en el alta). La API ya no
    //devuelve sipExtensionPassword, así que el control llega vacío y se manda vacío: el servidor
    //conserva entonces la guardada (UserResourceDefinition). Si se generase aquí, se le cambiaría
    //la contraseña del teléfono en cada guardado.
    this.userFormGroup.patchValue(this.user);

    this.userTypeSelected = this.user.userType;

    //el uso de API solo se registra para los usuarios de tipo API, así que en el resto no se ofrece
    if(this.userTypeSelected?.code == UsersDetailComponent.API_USER_TYPE_CODE){
      this.menuItems.push({ label: 'Uso de API', icon: 'pi pi-chart-line', command: () => this.apiUsagePanel()});
    }

    this.loadUserAuthenticationServices();
    this.loadUsers({filter: this.user?.secondUser?.name});
    
    if(this.userTypeSelected?.code=='technical') {
      if(this.user?.workerUserId) {
        this.loadUsersTechnical({filter: this.user?.workerUser?.name});
      }else{
        this.loadUsersTechnical();
      }
    }
   // this.loadGroups();
  }

  async loadLanguages() {
    let response = await firstValueFrom(this.languagesService.all());
    this.languages = response.data;
  }

  async loadUserTypes() {
    let response = await firstValueFrom(this.userTypesService.all({filter: FilterBuilder.fromObject({isSystem: new Filter(FilterOperation.equals, 'isSystem', "", false)}).build()}));
    this.userTypes = response.data;
  }

  async loadRoles() {
    let response = await firstValueFrom(this.rolesService.all());
    this.roles = response.data;
  }

  async loadUserAuthenticationServices() {
    let filters = {
      userId: new Filter(FilterOperation.equals, 'userId', "", this.user.id)
    };


    let params: IParamsCollection = {
      include: ['authenticationService']
    }
    //creamos filtros
    params.filter = FilterBuilder.fromObject(filters).build();

    let response = await firstValueFrom(this.usersAuthenticationServicesService.all(params));
    this.userAuthenticationServices = response.data;
    this.totalRecords = response.meta['total'];
  }

  async loadDDIs() {
    let response = await firstValueFrom(this.ddisService.all());
    this.ddis = response.data;
  }
/*
  async loadGroups() {
    let filters = {
      userId: new Filter(FilterOperation.equals, 'userId', "", this.user.id)
    };

    let params: IParamsCollection = {
      include: ['group']
    }

    //creamos filtros
    params.filter = FilterBuilder.fromObject(filters).build();

    let response = await firstValueFrom(this.groupUsersService.all(params));

    this.groups = response.data;
    this.totalGroupsRecords = response.meta['total'];
  }*/

  async save() {
    
    let valid = this.formValidationService.validateForm(this.userFormGroup);
    if (valid) {
      this.user = Object.assign(this.user, this.userFormGroup.value);

      if (!this.user.enableSIPExtension) {
        this.user.sipExtension = "";
        this.user.sipExtensionPassword = "";
      }
      //si no tenia id, convertimos el password a md5
      if (this.user.id == null || this.user.id == "") {
        this.user.password = this.stringUtils.ToMD5(this.user.password);
      }
      if(this.set_new_password && this.user.temporalPassword != null){
        this.user.password = this.stringUtils.ToMD5(this.user.temporalPassword);
        this.user.temporalPassword = null;
      }
      await this.usersService.save(this.user).subscribe({
        next: (data) => {
          this.messageService.add({ closable: true, severity: 'success', detail: "Usuario guardado correctamente." });
          this.set_new_password = false;
          
          this.router.navigate(['users/' + (data && Object.keys(data).length > 0 ? data.id : 0)]);
        },
        error: (error) => {
          //console.log(error);
          this.set_new_password = false;
        }
      });

    } else {
      this.messageService.add({ closable: true, severity: 'error', detail: "El formulario contiene errores." });
    }
  }

  async delete() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar el usuario?",
      header: "Eliminar usuario",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.usersService.delete(this.user.id).subscribe({
          next: (data: any) => {
            this.cancel();
            this.messageService.add({ closable: true, severity: 'success', detail: "Usuario eliminado correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }

  async editAuthService(userAuthenticationService: any) {
    let unselectedAuthServices: any[] = [];
    if (!userAuthenticationService) {
      unselectedAuthServices = this.userAuthenticationServices.map((uas: any) => uas.authenticationServiceId);
    } else {
      unselectedAuthServices = this.userAuthenticationServices.map((uas: any) => uas.authenticationServiceId).filter((x: any) => x != userAuthenticationService.authenticationServiceId);
    }

    let ref = this.dialogService.open(AuthenticationServiceComponent, {
      closable: true,
      modal: true,
      header: 'Editar servicio de autenticación',
      width: '400px',
      data: {
        userAuthenticationServiceId: userAuthenticationService ? userAuthenticationService.id : null,
        userId: this.user.id,
        authenticationServicesExist: unselectedAuthServices
      }
    });

    ref.onClose.subscribe((data: any) => {
      this.loadUserAuthenticationServices();
    });
  }

  async deleteAuthService(userAuthenticationService: any) {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar el servicio de autenticación?",
      header: "Eliminar usuario",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.usersAuthenticationServicesService.delete(userAuthenticationService.id).subscribe({
          next: (data: any) => {
            this.loadUserAuthenticationServices();
            this.messageService.add({ closable: true, severity: 'success', detail: "Servicio de autenticación eliminado correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      }
    });
  }
/*
  addGroup() {
    const ref = this.dialogService.open(UserGroupComponent, {
      closable: true,
      modal: true,
      data: {
        existentGroups: this.groups,
        userId: this.user.id
      },
      header: "Asignar grupo",
      width: '40%'
    });
    ref.onClose.subscribe((data: any) => {
      this.loadGroups();
    });
  }

  deleteGroup(userGroup: any) {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea desasignar el grupo " + userGroup.group?.name + "?",
      header: "Desasignar grupo",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.groupUsersService.delete(userGroup.id).subscribe({
          next: (data: any) => {
            this.loadGroups();
            this.messageService.add({ closable: true, severity: 'success', detail: "Grupo desasignado correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });

  }
*/
  cancel() {
    this.router.navigate(['/users']);
  }

  changeUserType(event: any) {
    this.userTypeSelected = this.userTypes.find((ut: UserType) => ut.id == event.value) as UserType;
    if (this.userTypeSelected.enableServiceEvaluations) {
      this.userFormGroup.controls['maxServiceEvaluatedPercent'].setValidators([Validators.required]);
    } else {
      this.userFormGroup.controls['maxServiceEvaluatedPercent'].setValue(0);
      this.userFormGroup.controls['maxServiceEvaluatedPercent'].clearValidators();
    }
    if(this.userTypeSelected?.code=='technical') this.loadUsersTechnical();
  }

  saveChangePassword(changeUserPassword: string){
    if(changeUserPassword != null && changeUserPassword != ""){
      this.user = Object.assign(this.user, this.userFormGroup.value);
      this.user.password = this.stringUtils.ToMD5(changeUserPassword);
      this.usersService.save(this.user).subscribe({
        next: (data: any) => {
          this.messageService.add({ closable: true, severity: 'success', detail: "Contraseña modificada correctamente." });
          this.visibleChangePass = false;
          this.password = "";
        },
        error: (error: any) => {
          //console.log(error);
        }
      });
    }
  }

  reassignCases(){
    this.confirmationService.confirm({
      message: "¿Está seguro que desea reasignar todos sus expedientes a otros tramitadores?",
      header: "Reasignar expedientes",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.usersService.reassignCases(this.user.id!).subscribe({
          next: (data: any) => {
           // this.loadGroups();
            this.messageService.add({ closable: true, severity: 'success', detail: "Expedientes reasignados correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }

  recoverCases(){
    this.confirmationService.confirm({
      message: "¿Está seguro que desea recuperar todos sus expedientes reasignados?",
      header: "Recuperar expedientes",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.usersService.recoverCases(this.user.id!).subscribe({
          next: (data: any) => {
           // this.loadGroups();
            this.messageService.add({ closable: true, severity: 'success', detail: "Expedientes recuperados correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }


  async userActivityReport() {
    this.router.navigate(['usersActivity'], {
      queryParams: { userId: this.user.id }
    });
  }

  /// Panel de uso de la API del usuario. Solo tiene datos para los usuarios de tipo API; el propio panel lo
  /// advierte si se llega a él con otro tipo.
  async apiUsagePanel() {
    this.router.navigate(['users', this.user.id, 'apiUsage']);
  }

  async findFreeExtension(){
    let response = await firstValueFrom(this.extensionService.findFreeExtension());
    if(response.extension!=null){
      this.user.sipExtension = response.extension;
      this.userFormGroup.get('sipExtension')?.setValue(response.extension);
      this.messageService.add({ closable: true, severity: 'success', detail: "Número de extensión libre encontrado: " + response.extension });
    }else{
      this.messageService.add({ closable: true, severity: 'error', detail: "No se ha encontrado un número de extensión libre." });
    }
  }

  //genera una nueva contraseña de extensión en edición y la deja a la vista: la API no devuelve la
  //guardada, así que este es el único momento en que el usuario puede copiarla para el teléfono/MicroSIP.
  generateSipExtensionPassword() {
    const password = this.stringUtils.RandomString(8);
    this.userFormGroup.get('sipExtensionPassword')?.setValue(password);
    this.userFormGroup.get('sipExtensionPassword')?.markAsDirty();
    this.generatedSipExtensionPassword = password;
  }

  async copyGeneratedSipExtensionPassword() {
    if (this.generatedSipExtensionPassword == null) return;
    try {
      await navigator.clipboard.writeText(this.generatedSipExtensionPassword);
      this.messageService.add({ closable: true, severity: 'success', detail: "Contraseña copiada al portapapeles." });
    } catch {
      this.messageService.add({ closable: true, severity: 'warn', detail: "No se ha podido copiar automáticamente; cópiala manualmente." });
    }
  }

    async loadUsers(event?: any) {

       let text = "";
       
      if (event != null) text = event.filter; 

      let filters: any = {
        userType: new Filter(FilterOperation.equals, 'userType.code', '', 'processor'),
        name: new Filter(FilterOperation.contains, 'name', '', text),
        active: new Filter(FilterOperation.equals, 'active', '', true)    
      };
      if(this.user && this.user.id) filters['id'] = new NotFilter(FilterOperation.equals, 'id', '', this.user.id);
      let params: IParamsCollection = {
        page: { size: 100, number: 1 },
        sort: ['name'],
        filter: FilterBuilder.fromObject(filters).build()
      };

    let response = await firstValueFrom(this.usersService.all(params));
    this.users = response.data;
  }

  async loadUsersTechnical(event?: any) {
    
    let text = "";       
    if (event != null) text = event.filter; 

    let filters = {
      name: new Filter(FilterOperation.contains, 'name', '', text),
      active: new Filter(FilterOperation.equals, 'active', '', true),
      userTypeId: new NotFilter(FilterOperation.equals, 'userType.code', '', 'technical')
    };
    let params: IParamsCollection = {
      page: { size: 100, number: 1 },
      sort: ['name'],
      filter: FilterBuilder.fromObject(filters).build()
    };

    let response = await firstValueFrom(this.usersService.all(params));
    this.usersTechnicals = response.data;
  }


  activeAbsence(){
    this.user.absence = this.userFormGroup.get('active')?.value == false;
    this.userFormGroup.get('absence')?.setValue(this.user.absence);
  }

}
